import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Slide,
} from '@mui/material';
import {
  CheckCircleOutline as SuccessIcon,
  ErrorOutline as ErrorIcon,
  WarningAmber as WarningIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import branding from '../../styles/branding';

const SEVERITY_CONFIG = {
  success: {
    icon: SuccessIcon,
    color: '#09ad40',
    defaultTitle: 'Success',
  },
  error: {
    icon: ErrorIcon,
    color: '#D32F2F',
    defaultTitle: 'Error',
  },
  warning: {
    icon: WarningIcon,
    color: '#F57C00',
    defaultTitle: 'Warning',
  },
  info: {
    icon: InfoIcon,
    color: '#1976D2',
    defaultTitle: 'Info',
  },
};

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CustomAlertContext = createContext(null);

/**
 * Provides showAlert and showConfirm functions to the entire app.
 *
 * showAlert(message, options?) — single "OK" button, returns Promise<void>
 * showConfirm(message, options?) — "Cancel" + "Confirm", returns Promise<boolean>
 *
 * Options: { title, severity, confirmText, cancelText }
 */
export const CustomAlertProvider = ({ children }) => {
  const [alertState, setAlertState] = useState({
    open: false,
    type: 'alert',
    title: '',
    message: '',
    severity: 'info',
    confirmText: 'OK',
    cancelText: 'Cancel',
  });

  const resolveRef = useRef(null);

  const showAlert = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      const severity = options.severity || 'info';
      setAlertState({
        open: true,
        type: 'alert',
        title: options.title || SEVERITY_CONFIG[severity]?.defaultTitle || 'Alert',
        message,
        severity,
        confirmText: options.confirmText || 'OK',
        cancelText: 'Cancel',
      });
    });
  }, []);

  const showConfirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      const severity = options.severity || 'warning';
      setAlertState({
        open: true,
        type: 'confirm',
        title: options.title || 'Confirm',
        message,
        severity,
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
      });
    });
  }, []);

  const handleClose = useCallback((result) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setAlertState((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <CustomAlertContext.Provider value={{ showAlert, showConfirm }}>
      {children}
      <CustomAlertDialog
        {...alertState}
        onConfirm={() => handleClose(true)}
        onCancel={() => handleClose(false)}
      />
    </CustomAlertContext.Provider>
  );
};

export const useCustomAlert = () => {
  const context = useContext(CustomAlertContext);
  if (!context) {
    throw new Error('useCustomAlert must be used within a CustomAlertProvider');
  }
  return context;
};

function CustomAlertDialog({
  open,
  type,
  title,
  message,
  severity,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
}) {
  const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.info;
  const SeverityIcon = config.icon;

  return (
    <Dialog
      open={open}
      onClose={type === 'alert' ? onConfirm : onCancel}
      maxWidth="xs"
      fullWidth
      TransitionComponent={SlideUp}
      PaperProps={{
        sx: {
          ...branding.dialog.PaperProps.sx,
          overflow: 'visible',
        },
      }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(4px)' },
        },
      }}
    >
      {/* Severity icon badge */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: -3,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
          }}
        >
          <SeverityIcon sx={{ fontSize: 32, color: config.color }} />
        </Box>
      </Box>

      <DialogTitle
        sx={{
          textAlign: 'center',
          pt: 2,
          pb: 0.5,
          px: 3,
        }}
      >
        <Typography
          variant="h6"
          component="span"
          sx={{
            fontFamily: branding.fonts.primary,
            fontWeight: 600,
            color: branding.colors.primary,
          }}
        >
          {title}
        </Typography>

        <IconButton
          onClick={type === 'alert' ? onConfirm : onCancel}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: 'rgba(0,0,0,0.4)',
            '&:hover': { color: 'rgba(0,0,0,0.7)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', px: 4, pt: 1, pb: 2 }}>
        <Typography
          variant="body1"
          sx={{
            fontFamily: branding.fonts.primary,
            color: branding.colors.text.secondary,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {message}
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'center',
          gap: 1.5,
          px: 3,
          pb: 3,
          pt: 0,
        }}
      >
        {type === 'confirm' && (
          <Button
            onClick={onCancel}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              px: 3,
              fontFamily: branding.fonts.primary,
              fontWeight: 500,
              color: branding.colors.text.secondary,
              border: '1px solid',
              borderColor: 'rgba(0,0,0,0.15)',
              '&:hover': {
                borderColor: 'rgba(0,0,0,0.3)',
                backgroundColor: 'rgba(0,0,0,0.03)',
              },
            }}
          >
            {cancelText}
          </Button>
        )}
        <Button
          onClick={onConfirm}
          variant="contained"
          autoFocus
          sx={{
            ...branding.button.sx,
            borderRadius: '20px',
            textTransform: 'none',
            px: 4,
            minWidth: 100,
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CustomAlertDialog;
